import { useMemo, useCallback, useEffect, useRef, useState } from 'react';
import { 
  ReactFlow, 
  Node, 
  ReactFlowProvider,
  Background,
  BackgroundVariant,
} from '@xyflow/react';
import { useQuery } from '@tanstack/react-query';
import { getSchemaLayout } from '../../api/schemas';
import { getConnections, getCableToTerminalConnections } from '../../api/connections';
import { 
  MainSchemaDeviceNode,
  MainSchemaActuatorNode,
  TerminalSchemaDeviceNode,
  MainSchemaTerminalNode
} from '../shared/nodes';
import { mapConnectionToEdge, buildAutoTerminalEdges } from '../shared/graph/edgeBuilder';
import type { ElectricConnection } from '../../types/connections';
import CustomEdge from '../CustomEdge';

interface SchemaLayoutItem {
  device_instance_id: number;
  position_x: number;
  position_y: number;
  specification_id: number;
  position_label?: string;
  description?: string;
  device_type?: string;
  device_type_code?: string;
  device_type_name?: string;
  custom_color?: string;
}
import '@xyflow/react/dist/style.css';

interface FullSchemaRendererProps {
  schemaType: 'main' | 'terminal';
  projectId: string;
  schemaId?: number;
  blockId?: number;
  showBackground?: boolean;
  viewportId?: number;
  onBoundsComputed?: (viewportId: number | undefined, bounds: { minX: number; minY: number; maxX: number; maxY: number }) => void;
  scrollX?: number;
  scrollY?: number;
  zoom?: number;
  onViewportChange?: (viewportId: number | undefined, updates: { scroll_x: number; scroll_y: number; zoom: number }) => void;
}

const nodeTypes = {
  device: MainSchemaDeviceNode,
  actuator: MainSchemaActuatorNode,
  cable: MainSchemaDeviceNode,
  terminalDevice: TerminalSchemaDeviceNode,
  terminalBlock: MainSchemaTerminalNode,
};

const edgeTypes = { custom: CustomEdge };

// Internal component that uses useReactFlow hook
function FullSchemaRendererContent({
  schemaType,
  projectId,
  schemaId,
  blockId,
  showBackground = false,
  viewportId,
  onBoundsComputed,
  scrollX = 0,
  scrollY = 0,
  zoom = 1,
  onViewportChange,
}: FullSchemaRendererProps) {
  void projectId;
  const lastBoundsRef = useRef<{ minX: number; minY: number; maxX: number; maxY: number } | null>(null);
  // Load schema layout
  const { data: schemaLayout = [] } = useQuery({
    queryKey: ['schemaLayout', schemaType, schemaId, blockId],
    queryFn: async () => {
      if (schemaType === 'main' && schemaId) {
        return getSchemaLayout(schemaId);
      } else if (schemaType === 'terminal' && blockId) {
        // For terminal schemas, we need to use a different API endpoint
        const response = await fetch(`/api/schemas/terminal/${blockId}/layout`);
        const data = await response.json();
        return data.data || [];
      }
      return [];
    },
    enabled: !!(schemaType === 'main' ? schemaId : blockId),
  });

  // Load connections
  const { data: connections = [] } = useQuery({
    queryKey: ['connections', schemaType, schemaId, blockId],
    queryFn: async () => {
      if (schemaType === 'main' && schemaId) {
        return getConnections(schemaId);
      } else if (schemaType === 'terminal' && blockId) {
        // For terminal schemas, we need to use a different API endpoint
        const response = await fetch(`/api/connections/terminal/${blockId}`);
        const data = await response.json();
        return data.data || [];
      }
      return [];
    },
    enabled: !!(schemaType === 'main' ? schemaId : blockId),
  });

  // Load cable to terminal connections for main schema
  const { data: cableTerminalConnections = [] } = useQuery({
    queryKey: ['cableTerminalConnections', schemaType, schemaId],
    queryFn: async () => {
      if (schemaType === 'main' && schemaId) {
        return getCableToTerminalConnections(Number(schemaId));
      }
      return [];
    },
    enabled: schemaType === 'main' && !!schemaId,
  });

  // Stable handlers for nodes
  const noop = useCallback(() => {}, []);
  
  // Convert schema layout to nodes
  const nodes = useMemo(() => {
    const layoutArray = Array.isArray(schemaLayout) 
      ? (schemaLayout as unknown as SchemaLayoutItem[])
      : ((schemaLayout as any)?.data ?? []);

    if (!Array.isArray(layoutArray) || layoutArray.length === 0) return [];

    // Normalize negative coordinates so nothing is clipped at x<0/y<0
    let minX = Infinity, minY = Infinity;
    for (const it of layoutArray) {
      if (typeof it.position_x === 'number' && it.position_x < minX) minX = it.position_x;
      if (typeof it.position_y === 'number' && it.position_y < minY) minY = it.position_y;
    }
    const PADDING = 50;
    const offsetX = Number.isFinite(minX) && minX < 0 ? -minX + PADDING : 0;
    const offsetY = Number.isFinite(minY) && minY < 0 ? -minY + PADDING : 0;

    return layoutArray.map((item: SchemaLayoutItem) => {
      const rawType = (item.device_type || item.device_type_code || item.device_type_name || '').toLowerCase();
      const nodeType = rawType.includes('cable') ? 'cable' : (rawType.includes('actuator') ? 'actuator' : 'device');
      
      return {
        id: item.device_instance_id.toString(),
        type: schemaType === 'terminal' ? 'terminalDevice' : nodeType,
        position: { x: item.position_x + offsetX, y: item.position_y + offsetY },
        data: {
          deviceInstanceId: item.device_instance_id,
          specificationId: item.specification_id,
          deviceName: item.position_label || '',
          description: item.description,
          deviceType: item.device_type || item.device_type_code || item.device_type_name || 'device',
          customColor: item.custom_color,
          onRemoveDevice: noop, // Not interactive in print mode
          onEditInstance: noop, // Not interactive in print mode
          onConnectionsChange: noop, // Not interactive in print mode
          printMode: true,
          hideHandles: false,
          contacts: [], // Empty contacts to prevent loading in print mode
        },
      } as Node;
    });
  }, [schemaLayout, schemaType, noop]);

  // Auto-generated terminal nodes for cable connections on all schemas except terminal block schemas
  const autoTerminalNodes = useMemo(() => {
    if (schemaType === 'terminal') return [] as Node[];
    if (!Array.isArray(cableTerminalConnections) || cableTerminalConnections.length === 0) return [] as Node[];

    // Group connections by cable to align terminals horizontally with spacing
    const byCable = new Map<number, any[]>();
    cableTerminalConnections
      .filter((c: any) => c.cable_device_type === 'cable')
      .forEach((c: any) => {
        const id = c.cable_instance_id;
        if (!byCable.has(id)) byCable.set(id, []);
        byCable.get(id)!.push(c);
      });

    const terminals: Node[] = [];
    byCable.forEach((connections: any[], cableId: number) => {
      if (connections.length === 0) return;

      // Locate cable node position from existing nodes
      const cableNode = nodes.find(n => n.id === String(cableId));
      if (!cableNode) return;

      // Sort by wire order extracted from cable_contact_number (e.g., "3-TOP")
      connections.sort((a: any, b: any) => {
        const aOrder = parseInt(String(a.cable_contact_number).split('-')[0]) || 0;
        const bOrder = parseInt(String(b.cable_contact_number).split('-')[0]) || 0;
        return aOrder - bOrder;
      });

      const contactsCount = connections.length;
      const cableWidth = contactsCount <= 4 ? 90 : Math.max(90, contactsCount * 18 + 20);
      const contactSpacing = 40;
      const terminalGroupWidth = (contactsCount - 1) * contactSpacing;
      const startX = cableNode.position.x + (cableWidth - terminalGroupWidth) / 2;
      const terminalY = cableNode.position.y - 160; // place above cable

      connections.forEach((connection: any, index: number) => {
        const terminalX = startX + index * contactSpacing;
        terminals.push({
          id: `terminal-${connection.terminal_instance_id}-${connection.terminal_contact_id}`,
          type: 'terminalBlock',
          position: { x: terminalX, y: terminalY },
          data: {
            terminal_instance_id: connection.terminal_instance_id,
            terminal_block_id: connection.terminal_block_id,
            terminal_block_name: connection.terminal_block_name,
            terminal_block_description: connection.terminal_block_description,
            terminal_position_in_block: connection.terminal_position_in_block,
            terminal_contact_id: connection.terminal_contact_id,
            terminal_opposite_contact_id: connection.terminal_opposite_contact_id,
            terminal_contact_label: connection.terminal_contact_label,
            terminal_opposite_contact_label: connection.terminal_opposite_contact_label,
            terminal_specification_id: connection.terminal_specification_id,
            cable_instance_id: connection.cable_instance_id,
            cable_contact_id: connection.cable_contact_id,
            connection_id: connection.connection_id,
            source_schema_id: connection.source_schema_id,
            cable_contact_number: connection.cable_contact_number,
            printMode: true,
          },
          draggable: false,
          selectable: false,
        } as Node);
      });
    });

    return terminals;
  }, [cableTerminalConnections, nodes, schemaType]);

  // Convert connections to edges
  const edges = useMemo(() => {
    if (!Array.isArray(connections) || connections.length === 0) return [];
    
    // Force custom edge for diagnostics/print to ensure CustomEdge renders (markers, manual path)
    const standardEdges = (connections as ElectricConnection[])
      .map((conn) => mapConnectionToEdge(conn, schemaType))
      .map((e: any) => ({ ...e, type: 'custom' }));
    
    if (schemaType !== 'terminal') {
      const autoEdges = buildAutoTerminalEdges(cableTerminalConnections as any[], [...nodes, ...autoTerminalNodes])
        .map((e: any) => ({ ...e, type: 'custom' }));
      // Смещение pathPoints под абсолютные координаты рендера (FullSchema без viewport-сдвига)
      autoEdges.forEach((e: any) => {
        if (e?.data?.pathPoints && Array.isArray(e.data.pathPoints)) {
          e.data.pathPoints = e.data.pathPoints.map((p: any) => ({ x: p.x, y: p.y, type: p.type || 'waypoint' }));
        }
      });
      return [...standardEdges, ...autoEdges];
    }
    
    return standardEdges;
  }, [connections, schemaType, cableTerminalConnections, nodes, autoTerminalNodes]);

  const allNodes = useMemo(() => {
    return [...nodes, ...autoTerminalNodes];
  }, [nodes, autoTerminalNodes]);

  // Compute bounds of all nodes and notify parent
  useEffect(() => {
    if (!onBoundsComputed) return;
    if (!allNodes || allNodes.length === 0) return;
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const n of allNodes) {
      const x = (n as any)?.position?.x ?? 0;
      const y = (n as any)?.position?.y ?? 0;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
    if (Number.isFinite(minX) && Number.isFinite(minY) && Number.isFinite(maxX) && Number.isFinite(maxY)) {
      const next = { minX, minY, maxX, maxY };
      const prev = lastBoundsRef.current;
      const changed = !prev || prev.minX !== next.minX || prev.minY !== next.minY || prev.maxX !== next.maxX || prev.maxY !== next.maxY;
      if (changed) {
        lastBoundsRef.current = next;
        onBoundsComputed(viewportId, next);
      }
    }
  }, [allNodes, onBoundsComputed, viewportId]);

  // Stable default viewport
  const defaultViewport = useMemo(() => ({ x: -Number(scrollX) || 0, y: -Number(scrollY) || 0, zoom: Number(zoom) || 1 }), [scrollX, scrollY, zoom]);

  // Force viewport update when scrollX/scrollY/zoom changes (for viewport persistence)
  const [viewportKey, setViewportKey] = useState(0);
  const lastViewportRef = useRef<{ x: number; y: number; zoom: number } | null>(null);
  
  useEffect(() => {
    if (scrollX !== undefined && scrollY !== undefined && zoom !== undefined) {
      const newViewport = { x: -Number(scrollX), y: -Number(scrollY), zoom: Number(zoom) };
      
      // Only update if values actually changed
      if (!lastViewportRef.current || 
          lastViewportRef.current.x !== newViewport.x ||
          lastViewportRef.current.y !== newViewport.y ||
          lastViewportRef.current.zoom !== newViewport.zoom) {
        
        lastViewportRef.current = newViewport;
        
        // Force ReactFlow to recreate with new viewport
        setViewportKey((prev: number) => prev + 1);
      }
    }
  }, [scrollX, scrollY, zoom]);

  return (
    <ReactFlow
      key={`${viewportId}-${viewportKey}`}
      nodes={allNodes}
      edges={edges}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      fitView={false}
      minZoom={0.01}
      maxZoom={10}
      defaultViewport={defaultViewport}
      zoomOnScroll={true}
      panOnScroll={false}
      zoomOnDoubleClick={true}
      panOnDrag={[0]}
      selectionOnDrag={false}
      nodesDraggable={false}
      nodesConnectable={false}
      elementsSelectable={false}
      onMove={(_, vp) => {
        // Sync RF viewport to parent (store as scroll_x/scroll_y/zoom)
        if (onViewportChange) {
          const sx = -Number(vp.x || 0);
          const sy = -Number(vp.y || 0);
          const zm = Number(vp.zoom || 1);
          onViewportChange(viewportId, { scroll_x: sx, scroll_y: sy, zoom: zm });
        }
      }}
      className="full-schema-renderer"
      style={{ width: '100%', height: '100%' }}
    >
      {showBackground && (
        <Background 
          variant={BackgroundVariant.Dots}
          gap={20}
          size={1}
          color="#ddd"
        />
      )}
    </ReactFlow>
  );
}

// Main component with ReactFlowProvider
export function FullSchemaRenderer(props: FullSchemaRendererProps) {
  return (
    <ReactFlowProvider key={`${props.schemaType}-${props.schemaId || props.blockId}`}>
      <FullSchemaRendererContent {...props} />
    </ReactFlowProvider>
  );
}
